import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { billsAPI, clientsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const emptyItem = () => ({ description: '', quantity: 1, unit_price: '' });

export default function NewBill() {
  const { id } = useParams();
  const isEdit = Boolean(id) && !window.location.pathname.includes('/new');
  const navigate = useNavigate();
  const { settings } = useSettings();
  const cur = settings.currency || 'BDT';

  const [form, setForm] = useState({
    client_name: '', client_address: '',
    bill_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: '',
    status: 'unpaid',
    discount: '', tax: '', notes: '',
  });
  const [items, setItems] = useState([emptyItem()]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clientsAPI.getAll()
      .then(res => setClients(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    billsAPI.getOne(id).then(res => {
      const b = res.data;
      setForm({
        client_name: b.client_name || '',
        client_address: b.client_address || '',
        bill_date: b.bill_date ? b.bill_date.split('T')[0] : '',
        due_date: b.due_date ? b.due_date.split('T')[0] : '',
        status: b.status || 'unpaid',
        discount: b.discount || '',
        tax: b.tax || '',
        notes: b.notes || '',
      });
      setItems(b.items?.length ? b.items.map(i => ({
        description: i.description, quantity: i.quantity, unit_price: i.unit_price
      })) : [emptyItem()]);
    }).catch(() => toast.error('Failed to load bill'));
  }, [id, isEdit]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleClientSelect = (e) => {
    const clientId = e.target.value;
    if (!clientId) return;
    const selected = clients.find(c => String(c.id) === clientId);
    if (selected) {
      setForm(f => ({
        ...f,
        client_name: selected.name,
        client_address: selected.address || '',
      }));
    }
  };

  const setItem = (idx, k, v) => {
    setItems(items => items.map((item, i) => i === idx ? { ...item, [k]: v } : item));
  };

  const addItem = () => setItems(i => [...i, emptyItem()]);
  const removeItem = idx => setItems(i => i.filter((_, ii) => ii !== idx));

  const subtotal = items.reduce((s, i) => s + ((parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0)), 0);
  const discount = parseFloat(form.discount) || 0;
  const tax = parseFloat(form.tax) || 0;
  const total = subtotal - discount + tax;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) return toast.error('Client name required');
    const validItems = items.filter(i => i.description.trim());
    if (!validItems.length) return toast.error('Add at least one item');

    setSaving(true);
    try {
      const payload = { ...form, items: validItems };
      if (isEdit) {
        await billsAPI.update(id, payload);
        toast.success('Bill updated!');
        navigate(`/bills/${id}`);
      } else {
        const res = await billsAPI.create(payload);
        toast.success('Bill created!');
        navigate(`/bills/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <Link to={isEdit ? `/bills/${id}` : '/bills'} className="back-link">← Back</Link>
      <h1 className="page-title">{isEdit ? 'Edit Bill' : 'New Bill'}</h1>

      <form onSubmit={handleSubmit}>
        {/* Client Info */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Client Information</span></div>
          <div className="card-body">

            {/* Client Dropdown */}
            <div className="form-group">
              <label className="form-label">Select Existing Client</label>
              <select className="form-control" onChange={handleClientSelect} defaultValue="">
                <option value="">-- Pick a client to auto-fill --</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.address ? ` — ${c.address}` : ''}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                Selecting a client auto-fills name and address below.
              </div>
            </div>

            <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0 16px' }} />

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input
                  className="form-control"
                  value={form.client_name}
                  onChange={e => setField('client_name', e.target.value)}
                  placeholder="Client or company name"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Client Address</label>
                <input
                  className="form-control"
                  value={form.client_address}
                  onChange={e => setField('client_address', e.target.value)}
                  placeholder="City, Country"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bill Details */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Bill Details</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Bill Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.bill_date}
                  onChange={e => setField('bill_date', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.due_date}
                  onChange={e => setField('due_date', e.target.value)}
                />
              </div>
              {isEdit && (
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control"
                    value={form.status}
                    onChange={e => setField('status', e.target.value)}
                  >
                    <option value="unpaid">Unpaid</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <span className="card-title">Items</span>
            <button type="button" className="btn btn-outline btn-sm" onClick={addItem}>+ Add Item</button>
          </div>
          <div className="card-body" style={{ padding: '12px 16px' }}>
            <div className="table-wrapper">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px', fontSize: 12, color: '#64748b', textAlign: 'left', width: 40 }}>SL</th>
                    <th style={{ padding: '8px', fontSize: 12, color: '#64748b', textAlign: 'left' }}>Description *</th>
                    <th style={{ padding: '8px', fontSize: 12, color: '#64748b', textAlign: 'left', width: 90 }}>Qty</th>
                    <th style={{ padding: '8px', fontSize: 12, color: '#64748b', textAlign: 'left', width: 130 }}>Unit Price</th>
                    <th style={{ padding: '8px', fontSize: 12, color: '#64748b', textAlign: 'right', width: 120 }}>Total</th>
                    <th style={{ width: 36 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
                    return (
                      <tr key={idx}>
                        <td style={{ padding: 6, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{idx + 1}</td>
                        <td style={{ padding: 6 }}>
                          <input
                            className="form-control"
                            style={{ minWidth: 180 }}
                            value={item.description}
                            onChange={e => setItem(idx, 'description', e.target.value)}
                            placeholder="Item description"
                          />
                        </td>
                        <td style={{ padding: 6 }}>
                          <input
                            type="number" min="0" step="0.01"
                            className="form-control"
                            value={item.quantity}
                            onChange={e => setItem(idx, 'quantity', e.target.value)}
                          />
                        </td>
                        <td style={{ padding: 6 }}>
                          <input
                            type="number" min="0" step="0.01"
                            className="form-control"
                            value={item.unit_price}
                            onChange={e => setItem(idx, 'unit_price', e.target.value)}
                            placeholder="0.00"
                          />
                        </td>
                        <td style={{ padding: 6, textAlign: 'right', fontWeight: 600, fontSize: 14 }}>
                          {lineTotal > 0 ? lineTotal.toLocaleString() : '-'}
                        </td>
                        <td style={{ padding: 6 }}>
                          {items.length > 1 && (
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={() => removeItem(idx)}
                              title="Remove"
                            >🗑</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <div style={{ minWidth: 280 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14, borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#64748b' }}>Subtotal</span>
                  <span style={{ fontWeight: 600 }}>{cur} {subtotal.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14, gap: 16, alignItems: 'center' }}>
                  <span style={{ color: '#64748b' }}>Discount</span>
                  <input
                    type="number" min="0" step="0.01"
                    className="form-control"
                    style={{ width: 120, textAlign: 'right' }}
                    value={form.discount}
                    onChange={e => setField('discount', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14, gap: 16, alignItems: 'center' }}>
                  <span style={{ color: '#64748b' }}>Tax / VAT</span>
                  <input
                    type="number" min="0" step="0.01"
                    className="form-control"
                    style={{ width: 120, textAlign: 'right' }}
                    value={form.tax}
                    onChange={e => setField('tax', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 4px', borderTop: '2px solid #0f172a' }}>
                  <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 16 }}>TOTAL</span>
                  <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 16 }}>{cur} {total.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header"><span className="card-title">Notes (Optional)</span></div>
          <div className="card-body">
            <textarea
              className="form-control"
              rows={3}
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              placeholder="Additional notes or payment instructions..."
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Link to={isEdit ? `/bills/${id}` : '/bills'} className="btn btn-outline">Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Bill' : 'Create Bill'}
          </button>
        </div>
      </form>
    </div>
  );
}